/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ErrorModel, OptionType } from '@/shared/api/BaseAPI.model'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import { t } from 'i18next'
import {
  categoryModel,
  categoryPayloadModel,
  categoryPutPayloadModel,
  QUERY
} from '../api/SidebarAccordionAPI.model'
import SidebarAccordionAPI from '../api/SidebarAccordionAPI'
import { useSelectedLanguagesQuery } from './useSelectedLanguagesQuery'
import { CATEGORY_IMAGES_ENABLED } from '../config'

export function useDishCategoriesQuery(categoryId?: string) {
  const queryClient = useQueryClient()
  const { showSnackbar } = useSnackbar()
  const currentLanguage =
    useTranslation().i18n.language || localStorage.getItem('i18nextLng') || ''
  const { defaultLanguage } = useSelectedLanguagesQuery(currentLanguage)

  const dishCategoriesList = useQuery({
    queryKey: [QUERY.DISH_CATEGORIES],
    queryFn: SidebarAccordionAPI.getDishCategories
  })

  const dishCategoriesOptions = useMemo(
    () =>
      dishCategoriesList.data?.map(category => ({
        label: category.translations.find(
          t => t.languageCode === defaultLanguage?.languageCode
        )?.name,
        value: category.id
      })) || [],
    [dishCategoriesList.data, defaultLanguage?.languageCode]
  ) as OptionType[]

  // Async: a category's picture is uploaded right after the save that created
  // or changed it, so the caller has to be able to await the save.
  const { mutateAsync: putDishCategoryAsync } = useMutation<
    categoryPutPayloadModel,
    ErrorModel,
    any
  >({
    mutationFn: (categoryPayload: categoryPutPayloadModel) =>
      SidebarAccordionAPI.putDishCategory(
        categoryPayload.categoryId,
        categoryPayload.categoryPayload
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY.DISH_CATEGORIES] })
      // The single-category query has its own key, so invalidating the list
      // leaves it stale and the edit form reopens on the pre-save name.
      // Removed rather than invalidated: the query client sets
      // `refetchOnMount: false`, so a stale entry would still be served as-is
      // to the next form; with no entry at all the reopen fetches.
      queryClient.removeQueries({ queryKey: [QUERY.DISH_CATEGORY] })
      showSnackbar(
        t('ALERTS.PUT_DISH_CATEGORY_SUCCESS.TITLE'),
        t('ALERTS.PUT_DISH_CATEGORY_SUCCESS.DESCRIPTION'),
        'success'
      )
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.PUT_DISH_CATEGORY_ERROR.TITLE'),
        t('ALERTS.PUT_DISH_CATEGORY_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  const { mutateAsync: postCategoryAsync } = useMutation<
    categoryModel,
    ErrorModel,
    any
  >({
    mutationFn: (categoryPayload: categoryPayloadModel) =>
      SidebarAccordionAPI.postDishCategory(categoryPayload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY.DISH_CATEGORIES] })
      showSnackbar(
        t('ALERTS.POST_DISH_CATEGORY_SUCCESS.TITLE'),
        t('ALERTS.POST_DISH_CATEGORY_SUCCESS.DESCRIPTION'),
        'success'
      )
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.POST_DISH_CATEGORY_ERROR.TITLE'),
        t('ALERTS.POST_DISH_CATEGORY_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  const { mutate: deleteCategory } = useMutation<string, ErrorModel, any>({
    mutationFn: (categoryId: string) =>
      SidebarAccordionAPI.deleteDishCategory(categoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY.DISH_CATEGORIES] })
      queryClient.removeQueries({ queryKey: [QUERY.DISH_CATEGORY] })
      showSnackbar(
        t('ALERTS.DELETE_DISH_CATEGORIES_SUCCESS.TITLE'),
        t('ALERTS.DELETE_DISH_CATEGORIES_SUCCESS.DESCRIPTION'),
        'success'
      )
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.DELETE_DISH_CATEGORIES_ERROR.TITLE'),
        t('ALERTS.DELETE_DISH_CATEGORIES_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  const category = useQuery({
    queryKey: [QUERY.DISH_CATEGORY, categoryId],
    queryFn: () => SidebarAccordionAPI.getDishCategory(categoryId!),
    enabled: !!categoryId
  })

  const categoryImage = useQuery({
    queryKey: [QUERY.DISH_CATEGORY_IMAGE, categoryId],
    queryFn: () => SidebarAccordionAPI.getDishCategoryImage(categoryId!),
    enabled: CATEGORY_IMAGES_ENABLED && !!categoryId
  })

  // Takes its target per call rather than closing over `categoryId`: a category
  // gets its picture right after being created, when the accordion still has no
  // id in state.
  const { mutateAsync: postCategoryImageAsync } = useMutation<
    void,
    ErrorModel,
    { file: File; categoryId: string }
  >({
    mutationFn: ({ file, categoryId: targetCategoryId }) => {
      const formData = new FormData()
      formData.append('file', file)
      return SidebarAccordionAPI.postDishCategoryImage(
        formData,
        targetCategoryId
      )
    },
    onSuccess: () => {
      // The list response carries the picture too, and the upload finishes
      // after the POST/PUT that refreshed it.
      queryClient.invalidateQueries({ queryKey: [QUERY.DISH_CATEGORIES] })
      queryClient.removeQueries({ queryKey: [QUERY.DISH_CATEGORY_IMAGE] })
      showSnackbar(
        t('ALERTS.POST_CATEGORY_IMAGE_UPLOAD_SUCCESS.TITLE'),
        t('ALERTS.POST_CATEGORY_IMAGE_UPLOAD_SUCCESS.DESCRIPTION'),
        'success'
      )
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.POST_CATEGORY_IMAGE_UPLOAD_ERROR.TITLE'),
        t('ALERTS.POST_CATEGORY_IMAGE_UPLOAD_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  const { mutate: deleteCategoryImage } = useMutation<void, ErrorModel, void>({
    mutationFn: () => SidebarAccordionAPI.deleteDishCategoryImage(categoryId!),
    onSuccess: () => {
      // Removed, not invalidated: the GET 404s once the picture is gone, and
      // react-query keeps the last successful data on a failed refetch, so the
      // form would go on showing it.
      queryClient.removeQueries({ queryKey: [QUERY.DISH_CATEGORY_IMAGE] })
      queryClient.invalidateQueries({ queryKey: [QUERY.DISH_CATEGORIES] })
      showSnackbar(
        t('ALERTS.DELETE_CATEGORY_IMAGE_SUCCESS.TITLE'),
        t('ALERTS.DELETE_CATEGORY_IMAGE_SUCCESS.DESCRIPTION'),
        'success'
      )
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.DELETE_CATEGORY_IMAGE_ERROR.TITLE'),
        t('ALERTS.DELETE_CATEGORY_IMAGE_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  return {
    dishCategoriesOptions,
    dishCategoriesList,
    putDishCategoryAsync,
    postCategoryAsync,
    deleteCategory,
    category,
    categoryImage,
    postCategoryImageAsync,
    deleteCategoryImage
  }
}
